import {
  createContext,
  type MutableRefObject,
  type ReactNode,
  useContext,
  useLayoutEffect,
} from "react";

import { useIsOverflowItemVisible } from "@fluentui/react-components";

/**
 * OverflowContext is a React context that holds a mutable reference to a Map, which is used to register the content of overflow items.
 * The keys of the Map are the IDs of the overflow items, and the values are the ReactNode content that should be displayed in the overflow menu when the item is not visible in the main area.
 * This context allows BuiOverflowItem components to register their content for use in the overflow menu when they are rendered within a BuiOverflow component.
 */
export const OverflowContext = createContext<
  MutableRefObject<Map<string, ReactNode>>
>({ current: new Map() });

/**
 * useOverflowItemContent is a custom hook that retrieves the content for an overflow item based on its ID and determines whether the content should be shown in the overflow menu.
 * @param id The ID of the overflow item.
 * @returns An object containing a boolean indicating whether the content should be shown in the overflow menu and the content itself.
 */
export function useOverflowItemContent(id: string): {
  showContent: boolean;
  content: ReactNode | undefined;
} {
  const isItemVisible = useIsOverflowItemVisible(id);
  const overflowRegistry = useContext(OverflowContext);
  const content = overflowRegistry.current.get(id);
  const showContent = !isItemVisible && !!content;
  return { showContent, content };
}

/**
 * useOverflowItemRegistration is a custom hook that registers the content of an overflow item in the OverflowContext when the component mounts and unregisters it when the component unmounts.
 * @param id The ID of the overflow item.
 * @param content The content of the overflow item to be registered.
 */
export const useOverflowItemRegistration = (
  id: string,
  content: ReactNode | undefined,
) => {
  const registry = useContext(OverflowContext);

  useLayoutEffect(() => {
    if (content === undefined) return;
    const currentRegistry = registry.current;
    currentRegistry.set(id, content);
    return () => {
      currentRegistry.delete(id);
    };
  }, [id, content, registry]);
};

// Re-exported from this subpath so consumers never need the package barrel for them: the
// barrel's only other route to these is the `@fluentui/react-components` pass-through in
// src/index.ts, and importing the barrel pulls every Bui* component into the eager graph.
export {
  useIsOverflowItemVisible,
  useOverflowMenu,
} from "@fluentui/react-components";
